import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { UUID } from '@/types';
import { TConsumptionCategory } from './types';

export type TSubscriptionDetailsResponse = {
  name: string;
  totalCredits: number;
};

export type TCreditsConsumptionInfoResponse = {
  totalConsumedCredits: number;
  consumption: TCreditsConsumptionInfo[];
};

export type TCreditsConsumptionInfo = {
  categoryName: TConsumptionCategory;
  type: string;
  consumedCredits: number;
  children: Nullable<TCreditsConsumptionInfo[]>;
};

export type TCreditConsumptionDetailsResponse = {
  consumerName: string;
  consumedCredits: number;
  consumerId: UUID;
  datasourceId: UUID;
}[];

export type TStripePriceTier = {
  from: number;
  upTo?: number;
  isCurrent: boolean;
  price: number;
  currentUsage: number;
};

export type TStripePriceTiersResponse = Record<string, TStripePriceTier[]>;

export type TCurrentBillingConsumption = {
  creditUsed: number;
  dateFrom: Date;
  dateTo: Date;
};

export type TPastBillingConsumption = {
  creditUsed: number;
  dateFrom: Date;
  dateTo: Date;
};

export const NONE = 'NONE';
export const NEAR_LIMIT = 'NEAR_LIMIT';
export const OVER_LIMIT = 'OVER_LIMIT';

export type TCreditConsumptionWarning =
  | typeof NONE
  | typeof NEAR_LIMIT
  | typeof OVER_LIMIT;

export const GET_SUBSCRIPTION_DETAILS = 'getSubscriptionDetails';
export const GET_CREDITS_CONSUMPTION_INFO = 'getCreditsConsumptionInfo';
export const GET_CREDITS_CONSUMPTION_DETAILS = 'getCreditsConsumptionDetails';
export const GET_CREDITS_CONSUMPTION_WARNING = 'getCreditConsumptionWarning';
export const GET_NEAREST_PRICE_TIERS = 'getNearestPriceTiers';
export const GET_NUMBER_OF_DATASOURCES_PER_SUBSCRIPTION =
  'getNumberOfDatasourcesPerSubscription';
export const GET_CURRENT_BILLING_CONSUMPTION = 'getCurrentBillingConsumption';
export const GET_PAST_BILLING_CONSUMPTION = 'getPastBillingConsumption';

export const creditsApi = createApi({
  reducerPath: 'creditsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/',
    credentials: 'same-origin',
  }),
  endpoints: (builder) => ({
    [GET_CREDITS_CONSUMPTION_INFO]: builder.query<
      TCreditsConsumptionInfoResponse,
      UUID
    >({
      query: (datasourceId) =>
        `datasources/${datasourceId}/credits-consumption-info`,
    }),
    [GET_SUBSCRIPTION_DETAILS]: builder.query<
      TSubscriptionDetailsResponse,
      UUID
    >({
      query: (datasourceId) =>
        `datasources/${datasourceId}/subscription-details`,
    }),
    [GET_CREDITS_CONSUMPTION_DETAILS]: builder.query<
      TCreditConsumptionDetailsResponse,
      {
        datasourceId: UUID;
        category: TConsumptionCategory;
        type: string;
      }
    >({
      query: ({ datasourceId, category, type }) =>
        `datasources/${datasourceId}/credits-consumption-info/${category}${
          type !== category ? `?type=${type}` : ''
        }`,
    }),
    [GET_CREDITS_CONSUMPTION_WARNING]: builder.query<
      TCreditConsumptionWarning,
      UUID
    >({
      query: (datasourceId) => `datasources/${datasourceId}/credit-warning`,
    }),
    [GET_NEAREST_PRICE_TIERS]: builder.query<TStripePriceTiersResponse, UUID>({
      query: (datasourceId) => `datasources/${datasourceId}/nearest-tiers`,
    }),
    [GET_NUMBER_OF_DATASOURCES_PER_SUBSCRIPTION]: builder.query<number, UUID>({
      query: (datasourceId) =>
        `datasources/${datasourceId}/get-count-by-subscription`,
    }),
    [GET_CURRENT_BILLING_CONSUMPTION]: builder.query<
      TCurrentBillingConsumption,
      UUID
    >({
      query: (datasourceId) =>
        `datasources/${datasourceId}/current-billing-consumption`,
      transformResponse: (res: TCurrentBillingConsumption) => {
        res.dateFrom = new Date(res.dateFrom);
        res.dateTo = new Date(res.dateTo);
        return res;
      },
    }),
    [GET_PAST_BILLING_CONSUMPTION]: builder.query<
      TPastBillingConsumption[],
      UUID
    >({
      query: (datasourceId) =>
        `datasources/${datasourceId}/past-billing-consumption`,
      transformResponse: (res: TPastBillingConsumption[]) =>
        res.map((item) => ({
          ...item,
          dateFrom: new Date(item.dateFrom),
          dateTo: new Date(item.dateTo),
        })),
    }),
  }),
});

export const {
  useGetCreditsConsumptionDetailsQuery,
  useGetCreditsConsumptionInfoQuery,
  useGetSubscriptionDetailsQuery,
  useGetCreditConsumptionWarningQuery,
  useGetNearestPriceTiersQuery,
  usePrefetch,
  useGetNumberOfDatasourcesPerSubscriptionQuery,
  useGetCurrentBillingConsumptionQuery,
  useGetPastBillingConsumptionQuery,
} = creditsApi;
